import React from 'react';

/* ── A5 landscape: 210mm × 148mm ── */
const S = {
  page: {
    fontFamily: 'Arial, sans-serif',
    fontSize: '10px',
    color: '#1a1a1a',
    width: '210mm',
    minHeight: '148mm',
    padding: '6mm 8mm',
    boxSizing: 'border-box',
    backgroundColor: '#fff'
  },
  /* ── header ── */
  header: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '2px solid #2e7d32',
    paddingBottom: '5px',
    marginBottom: '5px'
  },
  /* ── two-column info strip below header ── */
  infoStrip: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0 12px',
    marginBottom: '5px'
  },
  infoBox: {
    padding: '4px 6px',
    background: '#f9f9f9',
    borderRadius: '3px',
    border: '1px solid #e0e0e0'
  },
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '1.5px 0', fontSize: '10px' },
  infoLabel: { color: '#555', minWidth: '90px' },
  infoVal: { fontWeight: 600, textAlign: 'right' },
  sectionTitle: { fontWeight: 'bold', fontSize: '10px', margin: '4px 0 3px 0', color: '#2e7d32' },
  separator: { borderTop: '1px dashed #aaa', margin: '4px 0' },
  boldSeparator: { borderTop: '1.5px solid #333', margin: '4px 0' },
  /* ── items table ── */
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    borderBottom: '1.5px solid #333', borderTop: '1.5px solid #333',
    padding: '3px 4px', textAlign: 'left', fontSize: '10px',
    fontWeight: 'bold', backgroundColor: '#f0f4f0'
  },
  thRight: {
    borderBottom: '1.5px solid #333', borderTop: '1.5px solid #333',
    padding: '3px 4px', textAlign: 'right', fontSize: '10px',
    fontWeight: 'bold', backgroundColor: '#f0f4f0'
  },
  td: { padding: '3px 4px', fontSize: '10px', borderBottom: '1px solid #eee', verticalAlign: 'top' },
  tdRight: { padding: '3px 4px', fontSize: '10px', textAlign: 'right', borderBottom: '1px solid #eee' },
  /* ── totals ── */
  summaryRow: { display: 'flex', justifyContent: 'space-between', padding: '1.5px 0', fontSize: '10px' },
  totalRow: { display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '12px', fontWeight: 'bold' },
  /* ── payments history ── */
  payTable: { width: '100%', borderCollapse: 'collapse', marginTop: '3px' },
  payTh: {
    background: '#e8f5e9', borderBottom: '1px solid #ccc',
    padding: '2px 4px', fontSize: '9px', fontWeight: 'bold', textAlign: 'left'
  },
  payThRight: {
    background: '#e8f5e9', borderBottom: '1px solid #ccc',
    padding: '2px 4px', fontSize: '9px', fontWeight: 'bold', textAlign: 'right'
  },
  payTd: { padding: '2px 4px', fontSize: '9px', borderBottom: '1px solid #f0f0f0' },
  payTdRight: { padding: '2px 4px', fontSize: '9px', textAlign: 'right', borderBottom: '1px solid #f0f0f0' },
  chip: {
    display: 'inline-block', fontSize: '8px', padding: '0 3px',
    background: '#e8f4fd', borderRadius: '6px', marginLeft: '3px',
    color: '#1565c0', verticalAlign: 'middle'
  },
  footer: { textAlign: 'center', fontSize: '9px', color: '#666', marginTop: '5px' }
};

function fmt(num) {
  return `\u20B9${parseFloat(num || 0).toFixed(2)}`;
}

function BillPrint({ bill }) {
  if (!bill) return null;

  const isWalkin = !bill.patient_id && bill.walkin_name;
  const patientName = isWalkin
    ? bill.walkin_name
    : `${bill.first_name || ''} ${bill.last_name || ''}`.trim();
  const age  = bill.age   ? `${bill.age} yrs` : '';
  const gender = bill.gender ? (bill.gender.charAt(0).toUpperCase() + bill.gender.slice(1)) : '';
  const ageGender = [age, gender].filter(Boolean).join(' / ');

  const billDate = new Date(bill.bill_date || bill.created_at);
  const dateStr = billDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = billDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const consultationItem = bill.consultation_fee > 0 ? {
    description: 'Consultation Fee', quantity: 1,
    unit_price: bill.consultation_fee, cgst: 0, sgst: 0,
    total_amount: bill.consultation_fee, item_type: 'consultation', tax_rate: 0
  } : null;

  const allItems = consultationItem
    ? [consultationItem, ...(bill.items || [])]
    : (bill.items || []);

  const subtotalBeforeGst = parseFloat(bill.subtotal || 0);
  const cgst = parseFloat(bill.cgst || 0);
  const sgst = parseFloat(bill.sgst || 0);
  const totalGst = cgst + sgst;
  const discountAmount = parseFloat(bill.discount_amount || 0);
  const totalAmount = parseFloat(bill.total_amount || 0);
  const amountPaid = parseFloat(bill.amount_paid || 0);
  const balanceDue = totalAmount - amountPaid;

  const billTypeLabel =
    bill.bill_type === 'ipd'      ? 'IPD Admission Bill' :
    bill.bill_type === 'day_care' ? 'Day Care Bill'       : 'Invoice';

  /* payments history (from getBillWithPayments) */
  const payments = bill.payments || [];

  return (
    <div style={S.page}>

      {/* ── Header ── */}
      <div style={S.header}>
        <div style={{ width: '20mm', flexShrink: 0, textAlign: 'center' }}>
          <img src="/logo.png" alt="MSG Logo"
            style={{ width: '16mm', height: '16mm', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', padding: '0 4px' }}>
          <div style={{ fontSize: '14px', fontWeight: 900, color: '#1b5e20', letterSpacing: '0.5px' }}>
            MSG NATUROPATHY CENTER
          </div>
          <div style={{ fontSize: '8.5px', fontWeight: 700, color: '#2e7d32', marginTop: '1px' }}>
            (MANAGED BY ARZ UNIQUE ENTERPRISES LIMITED)
          </div>
          <div style={{ fontSize: '8.5px', color: '#444', marginTop: '2px' }}>
            Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351 &nbsp;|&nbsp; msgelite7wonders@gmail.com
          </div>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#1a237e', marginTop: '3px', letterSpacing: '1px' }}>
            {billTypeLabel.toUpperCase()}
          </div>
        </div>
        {/* <div style={{ width: '20mm', flexShrink: 0, textAlign: 'center' }}>
          <div style={{
            width: '16mm', height: '16mm', borderRadius: '50%', background: '#fff9e6',
            border: '2px solid #e65100', margin: '0 auto', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: '5.5px',
            fontWeight: 900, color: '#bf360c', lineHeight: 1.3, textAlign: 'center'
          }}>DERA<br />SACHA<br />SAUDA</div>
        </div> */}
      </div>

      {/* ── Info strip: Patient (left) | Invoice Meta (right) ── */}
      <div style={S.infoStrip}>
        {/* Patient Details */}
        <div style={S.infoBox}>
          <div style={{ fontSize: '9px', fontWeight: 700, color: '#2e7d32', marginBottom: '3px' }}>
            PATIENT DETAILS
          </div>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>Name:</span>
            <span style={S.infoVal}>{patientName}{isWalkin ? ' (Walk-in)' : ''}</span>
          </div>
          {bill.walkin_phone || bill.phone ? (
            <div style={S.infoRow}>
              <span style={S.infoLabel}>Phone:</span>
              <span style={S.infoVal}>{bill.walkin_phone || bill.phone}</span>
            </div>
          ) : null}
          {ageGender ? (
            <div style={S.infoRow}>
              <span style={S.infoLabel}>Age / Gender:</span>
              <span style={S.infoVal}>{ageGender}</span>
            </div>
          ) : null}
          {!isWalkin && bill.patient_code ? (
            <div style={S.infoRow}>
              <span style={S.infoLabel}>Patient Code:</span>
              <span style={S.infoVal}>{bill.patient_code}</span>
            </div>
          ) : null}
          {bill.bill_type === 'ipd' && bill.admission_id ? (
            <div style={S.infoRow}>
              <span style={S.infoLabel}>Admission ID:</span>
              <span style={S.infoVal}>{bill.admission_id}</span>
            </div>
          ) : null}
        </div>

        {/* Invoice Meta */}
        <div style={S.infoBox}>
          <div style={{ fontSize: '9px', fontWeight: 700, color: '#2e7d32', marginBottom: '3px' }}>
            INVOICE DETAILS
          </div>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>Invoice No.:</span>
            <span style={{ ...S.infoVal, color: '#1a237e' }}>{bill.bill_number}</span>
          </div>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>Date:</span>
            <span style={S.infoVal}>{dateStr}</span>
          </div>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>Time:</span>
            <span style={S.infoVal}>{timeStr}</span>
          </div>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>Payment Mode:</span>
            <span style={S.infoVal}>{(bill.payment_method || 'cash').toUpperCase()}</span>
          </div>
          <div style={S.infoRow}>
            <span style={S.infoLabel}>Status:</span>
            <span style={{
              ...S.infoVal,
              color: bill.payment_status === 'paid' ? '#2e7d32' :
                     bill.payment_status === 'partial' ? '#e65100' : '#c62828'
            }}>
              {(bill.payment_status || 'pending').toUpperCase()}
            </span>
          </div>
          {bill.generated_by_name && (
            <div style={S.infoRow}>
              <span style={S.infoLabel}>Generated By:</span>
              <span style={S.infoVal}>{bill.generated_by_name}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Items Table ── */}
      <table style={S.table}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: '45%' }}>Description</th>
            <th style={{ ...S.th, textAlign: 'center', width: '30px' }}>Qty</th>
            <th style={S.thRight}>Rate</th>
            <th style={S.thRight}>GST (5%)</th>
            <th style={S.thRight}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {allItems.map((item, idx) => {
            const qty = parseInt(item.quantity) || 1;
            const unitPrice = parseFloat(item.unit_price) || 0;
            const itemGst = parseFloat(item.cgst || 0) + parseFloat(item.sgst || 0);
            const total = parseFloat(item.total_amount) || 0;
            return (
              <tr key={idx}>
                <td style={S.td}>
                  {item.description}
                  {item.service_type && item.service_type !== 'general' && (
                    <span style={S.chip}>{item.service_type}</span>
                  )}
                  {item.item_type === 'package' && (
                    <span style={{ ...S.chip, background: '#fce4ec', color: '#880e4f' }}>pkg</span>
                  )}
                </td>
                <td style={{ ...S.td, textAlign: 'center' }}>{qty}</td>
                <td style={S.tdRight}>{fmt(unitPrice)}</td>
                <td style={S.tdRight}>{itemGst > 0 ? fmt(itemGst) : '—'}</td>
                <td style={S.tdRight}>{fmt(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Bottom: totals (right) + payments history (left) side by side ── */}
      <div style={S.boldSeparator} />
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>

        {/* Payments history (only if there are payments) */}
        {payments.length > 0 && (
          <div style={{ flex: 1 }}>
            <div style={{ ...S.sectionTitle, fontSize: '9px' }}>PAYMENT HISTORY</div>
            <table style={S.payTable}>
              <thead>
                <tr>
                  <th style={S.payTh}>Receipt No.</th>
                  <th style={S.payTh}>Date</th>
                  <th style={S.payThRight}>Amount</th>
                  <th style={S.payTh}>Method</th>
                  <th style={S.payTh}>Received By</th>
                </tr>
              </thead>
              <tbody>
                {payments.filter(p => p.transaction_type !== 'refund').map((p, i) => (
                  <tr key={i}>
                    <td style={S.payTd}>{p.payment_number || p.transaction_number}</td>
                    <td style={S.payTd}>{new Date(p.payment_date || p.transaction_date).toLocaleDateString('en-IN')}</td>
                    <td style={S.payTdRight}>{fmt(p.amount)}</td>
                    <td style={S.payTd}>{(p.payment_method || p.method || 'cash').toUpperCase()}</td>
                    <td style={S.payTd}>{p.received_by_name || p.staff_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Totals */}
        <div style={{ minWidth: '130px' }}>
          <div style={S.summaryRow}>
            <span style={{ color: '#555' }}>Subtotal (before GST):</span>
            <span>{fmt(subtotalBeforeGst)}</span>
          </div>
          {cgst > 0 && (
            <>
              <div style={S.summaryRow}>
                <span style={{ color: '#555' }}>CGST (2.5%):</span>
                <span>{fmt(cgst)}</span>
              </div>
              <div style={S.summaryRow}>
                <span style={{ color: '#555' }}>SGST (2.5%):</span>
                <span>{fmt(sgst)}</span>
              </div>
              <div style={S.summaryRow}>
                <span style={{ color: '#555' }}>Total GST (5%):</span>
                <span>{fmt(totalGst)}</span>
              </div>
            </>
          )}
          {discountAmount > 0 && (
            <div style={{ ...S.summaryRow, color: '#c62828' }}>
              <span>Discount{bill.discount_type === 'percent' ? ` (${bill.discount_value}%)` : ''}:</span>
              <span>- {fmt(discountAmount)}</span>
            </div>
          )}
          <div style={S.boldSeparator} />
          <div style={S.totalRow}>
            <span>Total:</span>
            <span>{fmt(totalAmount)}</span>
          </div>
          {amountPaid > 0 && (
            <div style={{ ...S.summaryRow, color: '#2e7d32' }}>
              <span>Paid:</span>
              <span>{fmt(amountPaid)}</span>
            </div>
          )}
          {balanceDue > 0.005 && (
            <div style={{ ...S.summaryRow, color: '#c62828', fontWeight: 'bold' }}>
              <span>Balance Due:</span>
              <span>{fmt(balanceDue)}</span>
            </div>
          )}
        </div>
      </div>

      {bill.notes && (
        <div style={{ marginTop: '4px', fontSize: '9px', color: '#555' }}>
          <strong>Note:</strong> {bill.notes}
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{ ...S.separator, marginTop: '6px' }} />
      <div style={S.footer}>
        Thank you for your visit! &nbsp;|&nbsp; MSG Naturopathy Center, Sirsa, Haryana
      </div>
    </div>
  );
}

export default BillPrint;
